/**
 * Schema-Based Validation Service
 * 
 * Service for validating form fields based on dynamic schema definitions and conditional field visibility.
 * Replaces static validation arrays with schema-driven validation that respects field dependencies.
 * 
 * Features:
 * - Dynamic field requirement validation based on visibility
 * - Type-specific validation (number, text, date, etc.)
 * - Schema-driven validation rules
 * - Conditional field dependency support
 * - Form-level validation with visible field filtering
 */

import { conditionalFieldService } from './conditionalFieldService';
import { fieldMetadataService } from './fieldMetadataService';

export const schemaValidationService = {
  /**
   * Validate if field is required based on schema and visibility
   * @param {string} fieldName - Name of the field to validate
   * @param {Object} formData - Current form data including type selection
   * @param {Object} schema - Form schema with field definitions
   * @returns {boolean} True if field is required and visible
   */
  isFieldRequired(fieldName, formData, schema) {
    const fieldMetadata = fieldMetadataService.getFieldMetadata(
      fieldName, 
      formData.type, 
      schema
    );
    
    if (!fieldMetadata) return false;
    
    // Field not visible = not required
    const isVisible = conditionalFieldService.evaluateFieldVisibility(
      { dependsOn: fieldMetadata.dependsOn }, 
      formData
    );
    
    if (!isVisible) return false;
    
    return fieldMetadata.required || false;
  },

  /**
   * Validate individual field value against schema rules
   * @param {string} fieldName - Name of the field to validate
   * @param {any} value - Field value to validate
   * @param {Object} formData - Current form data for context
   * @param {Object} schema - Form schema with validation rules
   * @returns {string|null} Error message if validation fails, null if valid
   */
  validateField(fieldName, value, formData, schema) {
    const fieldMetadata = fieldMetadataService.getFieldMetadata(
      fieldName, 
      formData.type, 
      schema
    );
    
    if (!fieldMetadata) return null;
    
    // Check required validation
    if (fieldMetadata.required && (!value || value.toString().trim() === '')) {
      return `${fieldMetadata.label} is required`;
    }
    
    // Apply type-specific validation
    return this.validateFieldType(value, fieldMetadata);
  },

  /**
   * Type-specific validation logic
   * @param {any} value - Field value to validate
   * @param {Object} fieldMetadata - Field metadata with type and validation rules
   * @returns {string|null} Error message if validation fails, null if valid
   */
  validateFieldType(value, fieldMetadata) {
    if (!value) return null;
    
    const { type, validation } = fieldMetadata;
    
    switch (type) {
      case 'number':
        if (isNaN(value)) return 'Must be a valid number';
        const numValue = Number(value);
        if (validation.min !== undefined && numValue < validation.min) {
          return `Must be at least ${validation.min}`;
        }
        if (validation.max !== undefined && numValue > validation.max) {
          return `Must be no more than ${validation.max}`;
        }
        break;
        
      case 'text':
        if (validation.pattern && !new RegExp(validation.pattern).test(value)) {
          return validation.message || 'Invalid format';
        }
        if (validation.maxLength && value.length > validation.maxLength) {
          return `Must be no more than ${validation.maxLength} characters`;
        }
        break;
        
      case 'date':
        const dateValue = new Date(value);
        if (isNaN(dateValue.getTime())) return 'Must be a valid date';
        break;
        
      case 'email':
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(value)) return 'Must be a valid email address';
        break;
        
      case 'tel':
        // Basic phone number validation
        const phonePattern = /^[\+]?[0-9\-\(\)\s]+$/;
        if (!phonePattern.test(value)) return 'Must be a valid phone number';
        break;
        
      case 'select':
        // Validate that value is in allowed options
        if (fieldMetadata.options && fieldMetadata.options.length > 0) {
          const validValues = fieldMetadata.options.map(opt => opt.value || opt);
          if (!validValues.includes(value)) {
            return `Please select a valid ${fieldMetadata.label.toLowerCase()}`;
          }
        }
        break;
    }
    
    return null;
  },

  /**
   * Validate entire form using schema
   * @param {Object} formData - Current form data
   * @param {Object} schema - Form schema with all field definitions
   * @returns {Object} Object with field names as keys and error messages as values
   */
  validateForm(formData, schema) {
    const errors = {};
    const visibleFields = conditionalFieldService.getVisibleFields(
      schema, 
      formData.type, 
      formData
    );
    
    visibleFields.forEach(fieldName => {
      const error = this.validateField(fieldName, formData[fieldName], formData, schema);
      if (error) {
        errors[fieldName] = error;
      }
    });
    
    return errors;
  },

  /**
   * Validate form step with step-specific field filtering
   * @param {number} step - Current form step
   * @param {Object} formData - Current form data
   * @param {Object} schema - Form schema
   * @param {Function} getFieldsForStep - Function to get fields for specific step
   * @returns {Object} Object with validation errors for the current step
   */
  validateStep(step, formData, schema, getFieldsForStep) {
    if (!schema) return {};
    
    // Get all form validation errors
    const allErrors = this.validateForm(formData, schema);
    
    // Filter errors to only those relevant for current step
    const stepFields = getFieldsForStep(step);
    const stepErrors = {};
    
    stepFields.forEach(fieldName => {
      if (allErrors[fieldName]) {
        stepErrors[fieldName] = allErrors[fieldName];
      }
    });
    
    return stepErrors;
  },

  /**
   * Check if form step is valid
   * @param {number} step - Current form step
   * @param {Object} formData - Current form data
   * @param {Object} schema - Form schema
   * @param {Function} getFieldsForStep - Function to get fields for specific step
   * @returns {boolean} True if step is valid (no errors)
   */
  isStepValid(step, formData, schema, getFieldsForStep) {
    const stepErrors = this.validateStep(step, formData, schema, getFieldsForStep);
    return Object.keys(stepErrors).length === 0;
  },

  /**
   * Get validation summary for debugging
   * @param {Object} formData - Current form data
   * @param {Object} schema - Form schema
   * @returns {Object} Validation summary with field counts and errors
   */
  getValidationSummary(formData, schema) {
    const visibleFields = conditionalFieldService.getVisibleFields(
      schema, 
      formData.type, 
      formData
    );
    
    const errors = this.validateForm(formData, schema);
    const requiredFields = visibleFields.filter(fieldName => 
      this.isFieldRequired(fieldName, formData, schema)
    );
    
    return {
      totalFields: visibleFields.length,
      requiredFields: requiredFields.length,
      errors: Object.keys(errors).length,
      errorFields: Object.keys(errors),
      visibleFields,
      requiredFields
    };
  }
};